import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Shared look for every box on the user Overview tab: each box is a card in the
 * app's card colour, and the page background shows between them (same colours
 * and border as AppCard, so it matches the rest of the dashboard).
 */
export const PANEL_SX = {
	bgcolor: 'card.background',
	border: '1px solid rgba(56, 56, 56, 1)',
	borderRadius: '3px',
} as const;

interface Props {
	title?: React.ReactNode;
	/** Small grey text on the right of the header. */
	caption?: React.ReactNode;
	/** Header-right slot for a button, e.g. Edit. */
	action?: React.ReactNode;
	children: React.ReactNode;
	sx?: SxProps<Theme>;
}

export default function OverviewPanel({ title, caption, action, children, sx }: Props) {
	return (
		<Paper
			elevation={0}
			sx={{ ...PANEL_SX, height: '100%', display: 'flex', flexDirection: 'column', ...sx }}
		>
			{title && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 1,
						px: 2,
						py: 1.25,
						borderBottom: '1px solid rgba(56, 56, 56, 1)',
					}}
				>
					<Typography variant="subtitle1" fontWeight={600} noWrap>
						{title}
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
						{caption && (
							<Typography variant="caption" color="text.secondary" noWrap>
								{caption}
							</Typography>
						)}
						{action}
					</Box>
				</Box>
			)}
			<Box sx={{ p: 2, flex: 1, minWidth: 0 }}>{children}</Box>
		</Paper>
	);
}

/** A small uppercase label above whatever it wraps; the rows of the info cards. */
export function PanelField({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<Box>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
				{label}
			</Typography>
			{children}
		</Box>
	);
}
